import {useCallback, useEffect, useRef} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {apiRequest} from '@/lib/queryClient';

interface AutoSaveOptions {
  debounceMs?: number;
  endpoint: string;
  method?: 'POST' | 'PUT' | 'PATCH';
  queryKeyToInvalidate?: string[];
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useDebouncedAutoSave<T>(options: AutoSaveOptions) {
  const {
    debounceMs = 2000, // 2-second debounce for better performance
    endpoint,
    method = 'POST',
    queryKeyToInvalidate,
    onSuccess,
    onError
  } = options;

  const queryClient = useQueryClient();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastSavedData = useRef<T>();
  const pendingData = useRef<T>();

  const saveMutation = useMutation({
    mutationFn: async (data: T) => {
      return await apiRequest(endpoint, {method, body: data});
    },
    onSuccess: () => {
      lastSavedData.current = pendingData.current;
      if (queryKeyToInvalidate) {
        queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate });
      }
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('Auto-save failed:', error);
      onError?.(error);
    }
  });

  const debouncedSave = useCallback((data: T) => {
    pendingData.current = data;
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Skip if data hasn't changed
    if (JSON.stringify(data) === JSON.stringify(lastSavedData.current)) {
      return;
    }

    // Set a new timeout
    timeoutRef.current = setTimeout(() => {
      if (pendingData.current) {
        saveMutation.mutate(pendingData.current);
      }
    }, debounceMs);
  }, [debounceMs, saveMutation]);

  const saveImmediately = useCallback((data: T) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    pendingData.current = data;
    saveMutation.mutate(data);
  }, [saveMutation]);

  const hasPendingChanges = useCallback(() => {
    return JSON.stringify(pendingData.current) !== JSON.stringify(lastSavedData.current);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    debouncedSave,
    saveImmediately,
    hasPendingChanges,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.error,
    lastSaveTime: saveMutation.isSuccess ? new Date() : null
  };
}
